import React, { useEffect, useState } from 'react'
import { GestureResponderEvent, SafeAreaView, TextInput as NativeTextInput, Platform } from 'react-native'
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil'
import { Checkbox, TextInput } from 'react-native-paper'
import { Authentication, ForgotPassword, GetUserByDocument, PortalContatos, PortalContatoPermissoes, PortalContatoClientes } from '@/domain/usecases'
import { LogoTheme } from '@/presentation/components/LogoTheme'
import { NavigationRoute } from '@/main/config/router-navigation'
import { KEY_ACCESS_PASSWORD, KEY_ACCESS_SELECTED_CONTACT, useAuth, useNotify, useTheme, useThemeClient } from '@/presentation/hooks'
import { BackgroundTheme, Box, Button, Divider, HStack, NavigationPagesProps, SocialMidias, Text, VStack, isOpenUpdatePasswordState, localStorageState, socialMediaState, isSelectingContactState } from '@/presentation/components'
import { ModalContatoSelect, ModalForgotPassword, ModalUserSelect } from '@/presentation/pages/Login/components'
import { styles } from './styles'
import * as State from '@/presentation/pages/Login/components/atoms'
import * as GeneralState from '@/presentation/components/Atoms'
import { version } from '../../../../package.json'
import QRCode from 'react-native-qrcode-svg'
import { PortalContatoClienteModel } from '@/domain/models'
import { InvalidCredentialsError } from '@/domain/errors'

type LoginAuthenticateProps = {
  username: string
  password: string
}

type LoginWithCodeOpts = {
  fetchContatosFor?: { email: string, senha: string }
}

type LoginProps = NavigationPagesProps & {
  getUser: GetUserByDocument
  authentication: Authentication
  forgotPassword: ForgotPassword
  getPortalContatos: PortalContatos
  getContatoPermissoes: PortalContatoPermissoes
  getContatoClientes: PortalContatoClientes
}

export const Login: React.FC<LoginProps> = (props) => {
  const theme = useTheme()
  const notify = useNotify()
  const { isAdmin, login, getSaveUser, saveUser, isPasswordSecure } = useAuth()
  const { getThemeClient } = useThemeClient()
  const [state, setState] = useRecoilState(State.loginState)
  const [isLoading, setLoading] = useRecoilState(State.isLoadingState)
  const [isSelected, setSelection] = useState(false)
  const setModalUserSelect = useSetRecoilState(State.usersSelectState)
  const setOpenUserSelect = useSetRecoilState(State.isOpenUserSelect)
  const setOpenForgotPassword = useSetRecoilState(State.isOpenForgotPasswordState)
  const setOpenUpdatePassword = useSetRecoilState(isOpenUpdatePasswordState)
  const prospectConfigs = useRecoilValue(GeneralState.prospectConfigsState)
  const localStorageAdapter = useRecoilValue(localStorageState)
  const prospectInterestArea = useRecoilValue(GeneralState.prospectInterestAreaState)
  const socialMedia = useRecoilValue(socialMediaState)
  const reset = useResetRecoilState(State.loginState)

  const setContatoClientesSelect = useSetRecoilState(State.contatoClientesSelectState)
  const setContatoClienteSelected = useSetRecoilState(State.contatoClienteSelectedState)
  const setPortalContato = useSetRecoilState(State.portalContatoState)
  const setIsSelectingContact = useSetRecoilState(isSelectingContactState)
  const [openContatoSelect, setOpenContatoSelect] = useState<boolean>(false)

  const savedUser = getSaveUser()
  const themeClient = getThemeClient()

  const [fieldsLabelColor, setFieldsLabelColor] = useState(theme.colors.white)
  const [fieldsValueColor, setFieldsValueColor] = useState(theme.colors.white)
  const [socialMediasColor, setSocialMediasColor] = useState(theme.colors.white)

  const handleChangeInput = (name: keyof LoginAuthenticateProps, value: string): void => {
    setState(currentValues => ({ ...currentValues, [name]: value }))
  }

  const handleError = (error: Error): void => {
    notify.show(error.message, { variant: 'error' })
  }

  const handleCheckSecurityPassword = (): void => {
    if (!isPasswordSecure(state.password) && !isAdmin()) {
      localStorageAdapter.set(KEY_ACCESS_PASSWORD, 'insecure_password')
      setOpenUpdatePassword(!isPasswordSecure(state.password))
      reset()
    }
  }

  const handleSubmit = (event?: GestureResponderEvent): void => {
    event?.preventDefault()

    if (!state.username) {
      handleError(new Error('O usuário deve ser informado'))
      return
    }

    if (!state.password) {
      handleError(new Error('A senha deve ser informada'))
      return
    }

    const dataAuth: Authentication.Params = {
      username: state.username,
      password: state.password
    }

    const isEmail = dataAuth.username.includes('@')
    const onlyDigits = dataAuth.username.replace(/\.|-/gm, '').replace(/\D/g, '')
    const isCpfOrCnpj = !isEmail && onlyDigits.length > 10

    if (isEmail || isCpfOrCnpj) {
      loginWithDocument(dataAuth)
    } else {
      loginWithCode(dataAuth)
    }
  }

  const buscarPortalContatos = async (email: string, senha: string): Promise<void> => {
    await props.getPortalContatos
      .get({ email, senha })
      .then(async (res) => {
        const contato = res.ClientesContato.at(0)

        if (!contato?.CodContato) {
          setIsSelectingContact(false)
          return
        }

        localStorageAdapter.set(KEY_ACCESS_SELECTED_CONTACT, contato)
        setPortalContato(contato)
        await buscarContatoClientes(contato.CodContato)
      })
      .catch(err => {
        handleError(err)
        setIsSelectingContact(false)
      })
  }

  const buscarContatoClientes = async (codContato: number): Promise<void> => {
    setLoading(true)
    await props.getContatoClientes
      .get({
        codContato
      })
      .then((res) => {
        if (res.length > 1) {
          setOpenContatoSelect(true)
          setContatoClientesSelect(res)
          return
        }

        const localStoragePortalContato = localStorageAdapter.get(KEY_ACCESS_SELECTED_CONTACT)

        setIsSelectingContact(false)
        localStorageAdapter.set(KEY_ACCESS_SELECTED_CONTACT, { ...localStoragePortalContato, CodCliente: res.at(0)?.CodCliente })
        setContatoClienteSelected(res.at(0) as PortalContatoClienteModel)
      })
      .catch(handleError)
      .finally(() => {
        setLoading(false)
      })
  }

  const loginWithCode = (params: Authentication.Params, opts?: LoginWithCodeOpts): void => {
    setLoading(true)
    setIsSelectingContact(true)

    props.authentication
      .auth(params)
      .then(async authToken => {
        login(authToken)

        if (opts?.fetchContatosFor) {
          await buscarPortalContatos(opts.fetchContatosFor.email, opts.fetchContatosFor.senha)
        } else {
          setIsSelectingContact(false)
        }

        if (params.username.toUpperCase() !== 'ADMIN' || !params.username.endsWith('@insidesistemas.com.br')) {
          handleCheckSecurityPassword()
          saveUser({
            username: isSelected ? params.username : '',
            saveUsername: isSelected
          })
        }
      })
      .catch((error) => {
        handleError(error)
        setIsSelectingContact(false)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const loginWithDocument = (params: Authentication.Params): void => {
    setLoading(true)

    props.getUser
      .getUsersByDocument({
        usuario: params.username,
        senha: params.password
      })
      .then(clients => {
        if (clients?.length === 1) {
          loginWithCode({
            username: clients[0].codCliente.toString(),
            password: params.password
          })
          handleCheckSecurityPassword()
        } else {
          setOpenUserSelect(true)
          setModalUserSelect(clients)
        }
      })
      .catch(err => {
        if (err instanceof InvalidCredentialsError && params.username.includes('@')) {
          loginWithCode(params, {
            fetchContatosFor: { email: params.username, senha: params.password }
          })
          return
        }
        handleError(err)
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    if (savedUser.saveUsername) {
      setState(currentState => ({ ...currentState, username: savedUser.username }))
      setSelection(true)
    }
  }, [])

  useEffect(() => {
    if (themeClient.loginFieldsLabelColor) {
      setFieldsLabelColor(themeClient.loginFieldsLabelColor)
    }
    if (themeClient.loginFieldsValueColor) {
      setFieldsValueColor(themeClient.loginFieldsValueColor)
    }
    if (themeClient.loginSocialMediasImgColor) {
      setSocialMediasColor(themeClient.loginSocialMediasImgColor)
    }
  }, [themeClient])

  const isWeb = Platform.OS === 'web'
  const isBrowser = typeof window !== 'undefined'
  const isLargeScreen = isBrowser && window.innerWidth > 768

  const showQrCode = isWeb && isBrowser && isLargeScreen

  return (
    <SafeAreaView style={{ ...styles.container, backgroundColor: themeClient.mainColor }}>
      <BackgroundTheme />

      <Box
        style={{
          ...styles.loginContent,
          backgroundColor: themeClient.mainColor,
          borderColor: theme.colors.greyDarken2
        }}
      >
        <LogoTheme height={100} />
        <Box style={styles.form}>
          <Text color={themeClient.loginColor} style={{ textAlign: 'center' }} variant="h6">Portal do Cliente</Text>
          <TextInput
            theme={{ colors: { onSurfaceVariant: fieldsLabelColor } }}
            textColor={fieldsValueColor}
            underlineColor={fieldsLabelColor}
            underlineColorAndroid={fieldsLabelColor}
            activeUnderlineColor={fieldsLabelColor}
            style={styles.input}
            label="Usuário"
            testID='login-usuario'
            disabled={isLoading}
            onChangeText={(text) => handleChangeInput('username', text)}
            value={state.username}
            render={(props) =>
              <NativeTextInput
                {...props}
                onKeyPress={(event: any) => {
                  event.key.toString().toLowerCase() === 'enter' && handleSubmit()
                }}
              />
            }
          />
          <TextInput
            theme={{ colors: { onSurfaceVariant: fieldsLabelColor } }}
            textColor={fieldsValueColor}
            underlineColor={fieldsLabelColor}
            underlineColorAndroid={fieldsLabelColor}
            activeUnderlineColor={fieldsLabelColor}
            style={styles.input}
            disabled={isLoading}
            label="Senha"
            testID='login-senha'
            secureTextEntry
            onChangeText={text => handleChangeInput('password', text)}
            value={state.password}
            render={(props) =>
              <NativeTextInput
                {...props}
                onKeyPress={(event: any) => {
                  event.key.toString().toLowerCase() === 'enter' && handleSubmit()
                }}
              />
            }
          />
          <Box style={styles.checkboxArea}>
            <Text color={fieldsLabelColor} variant="subtitle1">
              Salvar usuário
            </Text>
            <Checkbox
              uncheckedColor={themeClient.buttonSave}
              status={isSelected ? 'checked' : 'unchecked'}
              color={themeClient.buttonSave}
              onPress={() => setSelection(!isSelected)}
            />
          </Box>
          <Button
            style={{ width: '100%' }}
            color={themeClient.buttonSave}
            title="LOGIN"
            testID='button-login'
            onPress={handleSubmit}
            disabled={isLoading}
          />
          <HStack center>
            <Text
              color={fieldsLabelColor}
              variant="subtitle1"
              testID='button-esqueci-senha'
              onPress={() => setOpenForgotPassword(true)}
            >
              Esqueci minha senha
            </Text>
          </HStack>
        </Box>
        <Box>
          <SocialMidias socialMidias={socialMedia} color={socialMediasColor} />
          {prospectConfigs?.ativo && prospectInterestArea?.length > 0 &&
            <VStack spacing={8}>
              <Divider />
              <Text
                color={fieldsLabelColor}
                variant="subtitle1"
                testID='button-cadastre-se-aqui'
                onPress={() => { props.navigation.navigate(NavigationRoute.Prospect) }}
              >
                Ainda não é cliente? Cadastre-se aqui!
              </Text>
              {showQrCode &&
                <VStack center>
                  <QRCode value={window.location.href} size={75} />
                  <Text
                    color={fieldsLabelColor}
                    variant="subtitle1"
                  >
                    Acesse pelo celular
                  </Text>
                </VStack>
              }
            </VStack>
          }
        </Box>
      </Box>
      <ModalForgotPassword forgotPassword={props.forgotPassword} />
      <ModalUserSelect authentication={props.authentication} />
      <ModalContatoSelect
        isOpen={openContatoSelect}
        setOpen={setOpenContatoSelect}
        getContatoClientes={props.getContatoClientes}
        getPermissoes={props.getContatoPermissoes}
      />
      <Text style={styles.footer} color={theme.colors.white} variant="subtitle1">Desenvolvido por Inside Sistemas | v{version}</Text>
    </SafeAreaView>
  )
}
